import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";

import palette from "../../../common/styles/palette";

/**
 * "Checklist" — o instrumento de medida na íntegra, como o professor o autorou.
 *
 * É a leitura que faltava na aba Dados IA: o resultado da Entrevista não era
 * visível aqui — só o que a IA DEVOLVEU (relatório + rastro), nunca o que ela
 * RECEBEU. Cada Item aparece com enunciado, peso, sinal de crítico e as cinco
 * facetas de Detecção — o texto exato que o agente usa para julgar o vídeo.
 *
 * A fonte é `GET /checklist/{id}/debriefing/{id}` (a mesma chamada que o
 * DataIa já faz para juntar o enunciado ao relatório); o Checklist chega por
 * prop, sem segunda leitura.
 */

/** Mesma ordem e rótulos da Entrevista (RegrasDoItem) e do agente. */
const FACETAS: [string, string][] = [
  ["speech", "O que ouvir"],
  ["video", "O que ver"],
  ["correct_when", "Correto quando"],
  ["not_done_when", "Não realizou quando"],
  ["note", "Não penalizar por"],
];

/** A forma do checklist devolvida por `GET /checklist/{id}/debriefing/{id}`. */
export interface ChecklistIntegroItem {
  item_id?: number;
  question?: string;
  item_value?: number;
  critical?: boolean;
  detection?: Record<string, string> | null;
}

export interface ChecklistIntegroSkill {
  skill_id?: number;
  name?: string;
  points?: number;
  passing_score?: number;
  items?: ChecklistIntegroItem[];
}

export interface ChecklistIntegro {
  name?: string;
  skills?: ChecklistIntegroSkill[];
}

interface ChecklistIntegroProps {
  /** Resposta de `GET /checklist/{id}/debriefing/{id}`. */
  checklist: ChecklistIntegro | null;
}

const ChecklistIntegroSection: React.FC<ChecklistIntegroProps> = ({
  checklist,
}) => {
  if (!checklist) {
    return (
      <Typography variant="body2" sx={{ color: palette.textMuted }}>
        Checklist não disponível para esta estação.
      </Typography>
    );
  }

  const skills = checklist.skills || [];

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {/* Cabeçalho do instrumento. Sem peso nem corte: a aba Dados IA não
          mostra pontuação — o que ela mostra é o que a IA leu para julgar. */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 700, color: palette.text }}>
          {checklist.name}
        </Typography>
        <Typography variant="body2" sx={{ color: palette.textMuted }}>
          · {skills.length} habilidade(s)
        </Typography>
      </Box>

      {skills.map((skill, idx) => (
        <Box
          key={skill.skill_id ?? idx}
          sx={{
            borderRadius: "8px",
            border: `1px solid ${palette.border}`,
            backgroundColor: palette.surface,
            px: 2,
            py: 1.75,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: palette.brandDark,
              }}
            >
              {skill.name}
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gap: 1.5 }}>
            {(skill.items || []).map((item, iIdx) => {
              const facetas = (item.detection || {}) as Record<string, string>;
              const temDeteccao = FACETAS.some(
                ([chave]) => (facetas[chave] || "").trim()
              );

              return (
                <Box
                  key={item.item_id ?? iIdx}
                  sx={{
                    borderLeft: `3px solid ${item.critical ? palette.dangerText : palette.lilacMid}`,
                    pl: 1.75,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      {item.question}
                    </Typography>
                    {item.critical && (
                      <Chip
                        size="small"
                        label="CRÍTICO"
                        sx={{
                          height: 20,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color: palette.dangerText,
                          backgroundColor: `${palette.dangerText}18`,
                        }}
                      />
                    )}
                  </Box>

                  {/* A Detecção é o contrato com a IA: o texto que o agente
                      lê para decidir cada Item. Sem ela, o julgamento é por
                      intuição do modelo — e o professor precisa ver isso. */}
                  {temDeteccao ? (
                    <Box sx={{ mt: 0.75, display: "grid", gap: 0.5 }}>
                      {FACETAS.map(([chave, rotulo]) => {
                        const texto = (facetas[chave] || "").trim();
                        if (!texto) return null;
                        return (
                          <Box key={chave} sx={{ display: "flex", gap: 0.75, alignItems: "baseline" }}>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: palette.textMuted,
                                whiteSpace: "nowrap",
                                minWidth: 132,
                              }}
                            >
                              {rotulo}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: palette.text, whiteSpace: "pre-wrap" }}
                            >
                              {texto}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <RuleOutlinedIcon sx={{ fontSize: 14, color: palette.textMuted }} />
                      <Typography variant="caption" sx={{ color: palette.textMuted }}>
                        Sem regra de Detecção autorada — a IA julga este Item pelo enunciado.
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default ChecklistIntegroSection;
